import { cn } from "@/lib/utils";
import { InlineError, InlineSpinner, inputClasses, useInlineEdit, ViewSurface } from "./shared";

export type InlineOption = { value: string; label: string };

/**
 * A choice from a fixed set, edited in place.
 *
 * A select commits **on change** rather than on Enter: choosing is the whole interaction,
 * and asking for a second keystroke to confirm it is the ✓/✕ chrome this model exists
 * without. Escape and a blur that changed nothing still write nothing.
 *
 * `pending` is for a commit this component does not own — the stage select hands its choice
 * to the move flow, which may open a closure dialog before anything is sent. The spinner has
 * to follow the *flow's* progress, not this component's promise.
 */
export function InlineSelect({
  label,
  value,
  options,
  editable,
  disabled,
  pending = false,
  display,
  beneath,
  className,
  onCommit,
}: {
  label: string;
  value: string;
  options: InlineOption[];
  editable: boolean;
  disabled?: boolean;
  pending?: boolean;
  /** What the display surface shows. Defaults to the selected option's label. */
  display?: React.ReactNode;
  /** A line under the value — days in stage, a stalled indicator. Never editable. */
  beneath?: React.ReactNode;
  /** Passed to the display surface — a caller's layout constraint, not a style override. */
  className?: string;
  onCommit: (next: string) => Promise<unknown>;
}) {
  const field = useInlineEdit<string>({ value, equals: (a, b) => a === b, onCommit });

  if (!field.editing) {
    return (
      <div className="min-w-0">
        <ViewSurface
          label={label}
          editable={editable}
          disabled={disabled}
          saving={field.saving || pending}
          onActivate={field.begin}
          className={className}
        >
          {display ?? options.find((option) => option.value === value)?.label ?? value}
        </ViewSurface>
        {beneath}
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5">
        <select
          aria-label={label}
          value={field.draft}
          disabled={field.saving}
          onChange={(event) => {
            field.setDraft(event.target.value);
            void field.commit(event.target.value);
          }}
          onBlur={() => void field.commit(field.draft)}
          onKeyDown={(event) => {
            if (event.key !== "Escape") return;
            event.preventDefault();
            // Not propagated: abandoning the choice must not also close the modal.
            event.stopPropagation();
            field.cancel();
          }}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          className={cn(inputClasses("sm"), "pr-6")}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {field.saving && <InlineSpinner />}
      </div>
      {field.error && <InlineError message={field.error} />}
      {beneath}
    </div>
  );
}
